<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Notify } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useAuth } from 'src/boot/auth';
import {
  useStudentJoin,
  stashPendingJoin,
} from 'src/modules/School/composables/useStudentJoin';
import { joinErrorPresentation } from 'src/modules/School/utilities/joinErrorPresentation';
import SchoolJoinError from 'src/modules/School/components/SchoolJoinError/SchoolJoinError.vue';

// Orchestration for the invite-link join target. Performs the join via the composable
// and switches between a spinner (joining) and a human-readable error page.
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { currentUser } = useAuth();
const { joinStatus, joinError, runJoin } = useStudentJoin();

const classDocumentId = computed(() =>
  String(route.query.classDocumentId || ''),
);
const semesterDocumentId = computed(() =>
  String(route.query.semesterDocumentId || ''),
);

const presentation = computed(() => joinErrorPresentation(joinError.value));

async function attemptJoin(): Promise<void> {
  await runJoin(
    classDocumentId.value,
    semesterDocumentId.value || undefined,
  );
}

function goHome(): void {
  void router.push({ name: 'dashboard' });
}

onMounted(async () => {
  if (!classDocumentId.value) {
    await router.push({ name: 'school.login' });
    return;
  }

  if (!currentUser.value) {
    // Not signed in — stash the class + semester and send them to login;
    // consumed after sign-in.
    stashPendingJoin({
      classDocumentId: classDocumentId.value,
      ...(semesterDocumentId.value
        ? { semesterDocumentId: semesterDocumentId.value }
        : {}),
    });
    await router.push({ name: 'school.login' });
    return;
  }

  if (currentUser.value.role?.type !== 'student') {
    Notify.create({
      message: t('SCHOOL_JOIN_STUDENTS_ONLY'),
      color: 'negative',
    });
    await router.push({ name: 'school.home' });
    return;
  }

  await attemptJoin();
});
</script>

<template>
  <div
    v-if="joinStatus === 'joining'"
    class="flex flex-center q-pa-xl"
  >
    <q-spinner-gears
      size="60px"
      color="primary"
    />
  </div>

  <SchoolJoinError
    v-else
    :icon="presentation.icon"
    :title="t(presentation.titleKey)"
    :message="t(presentation.messageKey)"
    :show-retry="presentation.showRetry"
    @retry="attemptJoin"
    @home="goHome"
  />
</template>
